import { useQuery } from '@tanstack/react-query';
import { Battery, Gauge, Lock, Plus, Ruler, ShieldCheck } from 'lucide-react';
import { getBatteryModels } from '../api/client';
import { StatusBadge } from '../components/console/StatusBadge';
import { useI18n } from '../i18n';

export function BatteryModelsPage() {
  const { locale, t } = useI18n();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['battery-models'],
    queryFn: getBatteryModels,
  });

  return (
    <div className="mx-auto max-w-[1480px] space-y-6">
      <section className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-4xl font-bold text-console-text">{t('models.title')}</h1>
            <StatusBadge status="ONLINE" />
          </div>
          <p className="mt-2 text-console-muted">{t('models.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button className="console-button" type="button">
            <Lock size={16} />
            {t('models.closeLot')}
          </button>
          <button className="console-button console-button-primary" type="button">
            <Plus size={16} />
            {t('models.newModel')}
          </button>
        </div>
      </section>

      <section className="grid gap-5 md:grid-cols-3">
        <SummaryCard icon={Battery} label={t('models.configuredModels')} value={(data?.data.length ?? 0).toLocaleString(locale)} />
        <SummaryCard icon={ShieldCheck} label={t('models.approvalThresholds')} value="mAh" tone="text-console-good" />
        <SummaryCard icon={Gauge} label={t('models.environment')} value={t('models.local')} tone="text-console-blueSoft" />
      </section>

      {isLoading && <p className="font-mono text-console-muted">{t('models.loading')}</p>}
      {isError && <p className="text-console-defect">{t('models.loadError')}</p>}

      <section className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3">
        {data?.data.map((model) => (
          <article key={model.id} className="console-card p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="console-label">{t('models.batteryModel')}</p>
                <h2 className="mt-2 text-2xl font-bold text-console-blueSoft">{model.name}</h2>
                {model.description && <p className="mt-2 text-sm text-console-muted">{model.description}</p>}
              </div>
              <StatusBadge status="APPROVED" />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <ModelMetric icon={Gauge} label={t('models.voltage')} value={`${model.voltage}V`} />
              <ModelMetric icon={Battery} label={t('models.capacity')} value={`${model.capacityMah.toLocaleString(locale)} mAh`} />
            </div>

            <div className="mt-6 space-y-4">
              <Threshold label={t('models.approvalThreshold')} value={model.approvalThreshold} max={model.capacityMah} tone="bg-console-good" locale={locale} />
              <Threshold label={t('models.rejectionThreshold')} value={model.rejectionThreshold} max={model.capacityMah} tone="bg-console-defect" locale={locale} />
            </div>

            <div className="mt-6 rounded border border-console-border bg-console-base/60 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="console-label">{t('models.traceabilityConfig')}</span>
                <Ruler size={16} className="text-console-blueSoft" />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center font-mono text-xs">
                <span className="rounded border border-console-border py-2 text-console-good">{t('status.good')}</span>
                <span className="rounded border border-console-border py-2 text-console-half">{t('status.half')}</span>
                <span className="rounded border border-console-border py-2 text-console-defect">{t('status.defect')}</span>
              </div>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  tone = 'text-console-text',
}: {
  icon: typeof Battery;
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <section className="console-card p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="console-label">{label}</p>
          <p className={`console-value mt-3 text-3xl ${tone}`}>{value}</p>
        </div>
        <div className="rounded border border-console-border bg-console-panelHigh p-3 text-console-blueSoft">
          <Icon size={22} />
        </div>
      </div>
    </section>
  );
}

function ModelMetric({ icon: Icon, label, value }: { icon: typeof Battery; label: string; value: string }) {
  return (
    <div className="rounded border border-console-border bg-console-panelHigh p-4">
      <Icon className="mb-3 text-console-blueSoft" size={18} />
      <p className="console-label">{label}</p>
      <p className="console-value mt-2 text-lg">{value}</p>
    </div>
  );
}

function Threshold({ label, value, max, tone, locale }: { label: string; value: number; max: number; tone: string; locale: string }) {
  const width = max > 0 ? (value / max) * 100 : 0;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="console-label">{label}</span>
        <span className="console-value text-sm">{value.toLocaleString(locale)} mAh</span>
      </div>
      <div className="h-2 rounded-full bg-console-line">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.max(0, Math.min(100, width))}%` }} />
      </div>
    </div>
  );
}
